import { useEffect, useState, useRef } from 'react';
import api from '../config/api';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

// Notification sound function with proper initialization
let audioContextInitialized = false;
let audioContext = null;

const playNotificationSound = () => {
  try {
    // Initialize audio context on first use (requires user interaction)
    if (!audioContextInitialized) {
      audioContext = new (window.AudioContext || window.webkitAudioContext)();
      audioContextInitialized = true;
    }
    
    // Resume audio context if suspended (required after user interaction)
    if (audioContext && audioContext.state === 'suspended') {
      audioContext.resume().catch(() => {
        // Ignore resume errors - user interaction may be required
      });
    }
    
    if (!audioContext) {
      audioContext = new (window.AudioContext || window.webkitAudioContext)();
    }
    
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.value = 800; // Higher pitch for notification
    oscillator.type = 'sine';
    
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.3);
  } catch (error) {
    console.log('Could not play notification sound:', error);
  }
};

export const useMessageNotifications = () => {
  const [unreadCount, setUnreadCount] = useState(0);
  const { user, isAuthenticated } = useAuth();
  const lastCheckRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const backoffTimeoutRef = useRef(null);
  const backoffDelayRef = useRef(30000); // Start with 30 seconds
  const consecutiveErrorsRef = useRef(0);

  const checkUnreadMessages = async () => {
    if (!isAuthenticated || !user) return;

    try {
      const response = await api.get('/api/messages/unread-count');
      const newCount = response.data.unreadCount || 0;
      
      // Reset backoff on success
      consecutiveErrorsRef.current = 0;
      backoffDelayRef.current = 30000; // Reset to 30 seconds
      
      // If we're in backoff mode (longer interval), reset to normal interval
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = setInterval(() => {
          checkUnreadMessages();
        }, 60000); // Normal 60 second interval
      }
      
      // Show notification if count increased
      if (lastCheckRef.current !== null && newCount > lastCheckRef.current) {
        const newMessages = newCount - lastCheckRef.current;
        // Play notification sound
        playNotificationSound();
        toast(`You have ${newMessages} new message${newMessages > 1 ? 's' : ''}`, {
          icon: '💬',
          duration: 4000
        });
      }
      
      lastCheckRef.current = newCount;
      setUnreadCount(newCount);
    } catch (error) {
      consecutiveErrorsRef.current += 1;
      
      // Handle rate limit errors with exponential backoff
      if (error.response?.status === 429) {
        // Exponential backoff: 30s, 60s, 120s, max 5 minutes
        backoffDelayRef.current = Math.min(
          backoffDelayRef.current * 2,
          300000 // Max 5 minutes
        );
        
        // Only log first few errors to avoid spam
        if (consecutiveErrorsRef.current <= 3) {
          console.warn(`Rate limited. Backing off for ${backoffDelayRef.current / 1000}s`);
        }
        
        // Stop current polling
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        
        // Clear any existing backoff timeout
        if (backoffTimeoutRef.current) {
          clearTimeout(backoffTimeoutRef.current);
        }
        
        // Set backoff timeout - after backoff, restart polling
        backoffTimeoutRef.current = setTimeout(() => {
          backoffTimeoutRef.current = null;
          // Restart normal polling after backoff
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
          }
          pollIntervalRef.current = setInterval(() => {
            checkUnreadMessages();
          }, 30000); // 30 seconds after rate limit
          checkUnreadMessages(); // Immediate check after backoff
        }, backoffDelayRef.current);
        
        return;
      }
      
      // Silently fail for other errors - don't spam console
      if (consecutiveErrorsRef.current <= 5) {
        console.error('Error checking unread messages:', error.message);
      }
    }
  };

  useEffect(() => {
    if (!isAuthenticated || !user) {
      setUnreadCount(0);
      lastCheckRef.current = null;
      consecutiveErrorsRef.current = 0;
      backoffDelayRef.current = 30000;
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      if (backoffTimeoutRef.current) {
        clearTimeout(backoffTimeoutRef.current);
        backoffTimeoutRef.current = null;
      }
      return;
    }

    // Initial check
    checkUnreadMessages();

    // Poll every 60 seconds to reduce API calls and prevent rate limiting
    pollIntervalRef.current = setInterval(() => {
      checkUnreadMessages();
    }, 60000); // 60 seconds - reduces API calls and prevents rate limiting

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
      if (backoffTimeoutRef.current) {
        clearTimeout(backoffTimeoutRef.current);
      }
    };
  }, [isAuthenticated, user]);

  return { unreadCount };
};

